import {
  DragDropContext,
  Draggable,
  type DraggableProvidedDragHandleProps,
  type DraggableProvidedDraggableProps,
  type DropResult,
  Droppable,
  type DroppableProps,
} from '@hello-pangea/dnd';
import { Box, type SxProps, type Theme } from '@mui/material';
import { type ReactElement, type ReactNode, useEffect, useState } from 'react';

import { hasValue } from '@core/utils/equality';

const StrictModeDroppable = ({ children, ...props }: DroppableProps): ReactElement | null => {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setEnabled(true));
    return (): void => {
      cancelAnimationFrame(frame);
      setEnabled(false);
    };
  }, []);
  if (!enabled) return null;
  return <Droppable {...props}>{children}</Droppable>;
};

export type SlabDragControl = DraggableProvidedDragHandleProps | null;
export type SlabDraggableProvidedDraggableProps = DraggableProvidedDraggableProps;
export type SlabDraggableRef = (element?: HTMLElement | null) => void;

interface SlabBaseDraggableProps<T> {
  renderItem: (params: { item: T; index: number; dragControls: SlabDragControl }) => ReactNode;
}

export interface SlabDropResult<T> extends DropResult {
  reordered: T[];
}

interface SlabDraggableItemProps<T> extends SlabBaseDraggableProps<T> {
  item: T;
  id: string;
  index: number;
}

const SlabDraggableItem = <T,>({
  id,
  item,
  index,
  renderItem,
}: SlabDraggableItemProps<T>): ReactElement => {
  return (
    <Draggable draggableId={id} index={index}>
      {(provided) => (
        <Box ref={provided.innerRef} {...provided.draggableProps}>
          {renderItem({
            item,
            index,
            dragControls: provided.dragHandleProps,
          })}
        </Box>
      )}
    </Draggable>
  );
};

interface SlabDraggableListProps<T> extends SlabBaseDraggableProps<T> {
  data: T[];
  keyExtractor(item: T, index: number): string;
  onReorder(props: SlabDropResult<T>): void;
  sx?: SxProps<Theme>;
  droppableId: DroppableProps['droppableId'];
  direction: DroppableProps['direction'];
}

export const SlabDraggableList = <T,>({
  data,
  renderItem,
  keyExtractor,
  onReorder,
  sx,
  ...droppableProps
}: SlabDraggableListProps<T>): ReactElement => {
  const onDragEnd = (result: DropResult): void => {
    if (!hasValue(result.destination)) return;

    const reordered = Array.from(data);
    const [movedItem] = reordered.splice(result.source.index, 1);

    reordered.splice(result.destination.index, 0, movedItem);

    onReorder({ ...result, reordered });
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <StrictModeDroppable {...droppableProps}>
        {(provided) => (
          <Box sx={sx} ref={provided.innerRef} {...provided.droppableProps}>
            {data.map((item, index) => {
              const key = keyExtractor(item, index);

              return (
                <SlabDraggableItem
                  id={key}
                  key={key}
                  item={item}
                  index={index}
                  renderItem={renderItem}
                />
              );
            })}
            {provided.placeholder}
          </Box>
        )}
      </StrictModeDroppable>
    </DragDropContext>
  );
};
